import get from "lodash/get";
import React, { useState } from "react";
import { Button, Modal, Pagination, PlusIcon } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { useForm } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import PKDUploadFileForm from "~/components/Forms/PKDUploadFileForm";
import PKDFilesFilter from "~/pages/PKD/PKDFiles/Filter";
import Wrapper from "~/shared/ui/Wrapper";
import { DealerInfoPanel } from "~/entity/Dealer/ui/DealerInfoPanel";
import {
  useGetLocationData,
  useGoToPreviousPage,
  useSetLocationData,
} from "~/hooks/locationData";
import {
  getEndOfDayWithTimestamp,
  getFullTimeWithTimestamp,
} from "~/libs/getDateOptions";
import { getFormatDate } from "~/libs/getFormatDate";
import {
  useDeleteFileCatalogMutation,
  useEditFileCatalogMutation,
  useGetFileCatalogQuery,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useCurrentDealerId } from "~/shared/hooks/useCurrentDealerId";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { ConfirmModal } from "~/shared/ui/ConfirmModal";
import { TableV2 } from "~/shared/ui/TableV2";

import { getPKDFilesColumns } from "./model/tableAdapter";
import { FILE_OPTIONS } from "./options";

import s from "./PKDFiles.less";

const uploadFileFormId = "PKDUploadFileForm";

const PKDFiles = () => {
  const { t } = useTranslation();
  const {
    openModal,
    event,
    count,
    page,
    currentId,
    sortType,
    sortField,
    searchText,
    UserId,
    dateBegin,
    dateEnd,
  } = useGetLocationData();
  const { hasPermission } = usePermissions();
  const currentDealerId = useCurrentDealerId();
  const setData = useSetLocationData();
  const isAdd = openModal === "Add";
  // id загруженного в форме файла — управляет доступностью okButton модалки
  const [uploadedFileId, setUploadedFileId] = useState<string | null>(null);
  const location = useLocation();
  const goBack = useGoToPreviousPage();

  // Сегмент :name роута /pkd/:name/:name — ключ заголовка страницы
  const pathSegments = location.pathname.split("/");

  const openAddModal = () => {
    setData("openModal", "Add");
  };

  // control/handleSubmit нужны адаптеру колонок для инлайн-переименования файла
  const { handleSubmit, control } = useForm();

  const { handleCount, handlePage } = usePaginationParams();

  const handleCloseModal = () => {
    setData("openModal", "");
    setUploadedFileId(null);
  };

  const dateNow = new Date(new Date().setHours(23, 59, 59, 0));

  const { data, refetch } = useGetFileCatalogQuery({
    variables: {
      count,
      page,
      DealerId: currentDealerId,
      catalog: event,
      UserId,
      sortType,
      sortField,
      dateBegin: dateBegin
        ? getFullTimeWithTimestamp(dateBegin)
        : new Date(2000, 0, 1),
      dateEnd: dateEnd ? getEndOfDayWithTimestamp(dateEnd) : dateNow,
      name: searchText,
    },
    fetchPolicy: "no-cache",
  });

  const fileCatalog = get(data, "FileCatalogGetter.getFiles.items", []).map(
    (file) => {
      const { User, createdAt } = file;

      return {
        ...file,
        createdAt: getFormatDate(createdAt),
        fullname: User.fullname || "",
      };
    },
  );
  const total = get(data, "FileCatalogGetter.getFiles.total", 0);

  const [deleteFileMutation] = useDeleteFileCatalogMutation({
    variables: { FileId: String(currentId) },
    onCompleted() {
      refetch();
    },
  });

  const [editFileMutation] = useEditFileCatalogMutation({
    onCompleted() {
      setData("openModal", "", "currentId", "");
      refetch();
    },
  });

  const deleteFile = () => {
    deleteFileMutation();
    handleCloseModal();
  };

  const onSubmit = (data) => {
    editFileMutation({
      variables: {
        FileId: String(currentId),
        catalog: event,
        name: data.name,
      },
    });
  };

  const handleFileDownload = (record: { link?: string }) => {
    if (!record.link) return;
    const a = document.createElement("a");
    a.href = record.link;
    a.setAttribute("download", "");
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleRowDeleteClick = (record: { id: string | number }) => {
    setData("openModal", "Sure", "currentId", record.id);
  };

  const handleRowEditClick = (record: { id: string | number }) => {
    setData("openModal", "edit", "currentId", record.id);
  };

  return (
    <Wrapper.Content
      title={t(`pkd.${pathSegments[3]}`)}
      txtBtn={t("ui.back")}
      goBackHandler={goBack}
      filterComponent={<PKDFilesFilter />}
      controlComponent={
        <Flex gap={10}>
          {hasPermission("pkd.uploadFile") && (
            <Button
              onClick={openAddModal}
              size={"middle"}
              icon={<PlusIcon size={16} />}
            >
              {t("ui.upload")}
            </Button>
          )}
        </Flex>
      }
    >
      <div className={s.dealerInfoPanel}>
        <DealerInfoPanel />
      </div>
      <TableV2
        columns={getPKDFilesColumns(t, control, handleSubmit, onSubmit)}
        dataSource={fileCatalog}
        rowKey="id"
        scroll={{ x: "max-content" }}
        sortableColumns={FILE_OPTIONS}
        sortField={sortField}
        sortType={sortType as "ASC" | "DESC"}
        onSortChange={(field, type) =>
          setData("sortField", field ?? "", "sortType", type ?? "")
        }
        wrapperClassName={s.filesTable}
        onRowEdit={
          hasPermission("pkd.editFile") ? handleRowEditClick : undefined
        }
        onDownload={handleFileDownload}
        downloadButtonTitle={t("pkd.download")}
        onRowDelete={
          hasPermission("pkd.deleteFile") ? handleRowDeleteClick : undefined
        }
        actionsFixed
      />
      <Pagination
        count={count}
        page={page + 1}
        totalCount={total}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />
      <Modal
        open={isAdd}
        title={t("pkd.upload_file")}
        onCancel={handleCloseModal}
        closable
        centered
        destroyOnHidden
        cancelButton={{ children: t("ui.cancel") }}
        okButton={{
          htmlType: "submit",
          form: uploadFileFormId,
          children: t("ui.upload_file"),
          disabled: !uploadedFileId,
        }}
      >
        <PKDUploadFileForm
          setCloseModal={handleCloseModal}
          refetch={refetch}
          formId={uploadFileFormId}
          submitInForm={false}
          onFileChange={setUploadedFileId}
        />
      </Modal>

      <ConfirmModal
        open={openModal === "Sure"}
        title={t("events.delete_files")}
        onConfirm={deleteFile}
        onCancel={handleCloseModal}
        closable
        centered
        destroyOnHidden
      />
    </Wrapper.Content>
  );
};

export default PKDFiles;
